import { useCallback, useMemo, useRef } from "react";

import { getNextQueryParams } from "components/helpers";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import MapModal from "components/MapModal";
import CaseEventService from "services/CaseEvent";
import { useCaseEventsTable } from "hooks/useCaseEventsTable";
import { CASE_EVENTS_TABLE_CONSTANTS } from "./CaseEventsTableV3Constants";
import { omitByNil } from "helpers/tables";

const downloadConfig = {
  option: {
    label: "Include raw event data",
    description:
      "Adds the columns from the original imported file that the events table does not show, such as Session ID, Computer ID, and Custom1 to Custom5. The file will have many more columns.",
    params: {
      include_raw_data: true,
    },
  },
};

const CaseEventsTableV3 = ({ caseId, productLineId }) => {
  const mapModalRef = useRef(null);

  const defaultQueryParams = useMemo(() => {
    return omitByNil({
      case_id: caseId,
      product_line_id: productLineId,
    });
  }, [caseId, productLineId]);

  const openMapModal = useCallback(data => {
    mapModalRef.current.open(data);
  }, []);

  const { filters, columns, queryKey, invalidateData } = useCaseEventsTable({
    caseId,
    productLineId,
    openMapModal,
  });

  const fetchCaseEvents = useCallback(
    async queryParams => {
      try {
        const nextQueryParams = getNextQueryParams(queryParams, columns);

        const response = await CaseEventService.getTable({
          ...nextQueryParams,
          ...defaultQueryParams,
        });

        return response;
      } catch (error) {
        console.error("Error fetching case events:", error);
        throw error;
      }
    },
    [columns, defaultQueryParams]
  );

  return (
    <>
      <MapModal hasAddresses={false} ref={mapModalRef} />
      <BaseTableV3
        columns={columns}
        filters={filters}
        defaultQueryParams={defaultQueryParams}
        queryKey={queryKey}
        className={CASE_EVENTS_TABLE_CONSTANTS.className}
        exportTableName={CASE_EVENTS_TABLE_CONSTANTS.exportTableName}
        tableName={CASE_EVENTS_TABLE_CONSTANTS.tableName}
        header={CASE_EVENTS_TABLE_CONSTANTS.header}
        headerIcon={CASE_EVENTS_TABLE_CONSTANTS.headerIcon}
        downloadConfig={downloadConfig}
        downloadParams={defaultQueryParams}
        enableSearch
        enableSettings
        fetchData={fetchCaseEvents}
        invalidateData={invalidateData}
      />
    </>
  );
};

export default CaseEventsTableV3;
